import createReducer from 'redux/lib/reducerConfig';
import { showNotification } from 'common/function';
import { reportError } from 'utils/reporting';
import { SurveyJobService, SurveyService } from 'types';
import { runCancelableQuery } from 'utils/runCancelableQuery';
import { GLOBAL_LEVEL_NOT_APPLICABLE } from 'common/constants';

const ActionTypes = {
  SET_SURVEY_DATA: 'MARKET_PRICE/SET_SURVEY_DATA',
  RESET_SURVEY_DATA: 'MARKET_PRICE/RESET_SURVEY_DATA',
  SET_SURVEY_FILTER_DATA: 'MARKET_PRICE/SET_SURVEY_FILTER_DATA',
  SET_SURVEY_EFFECTIVE_DATES: 'MARKET_PRICE/SET_SURVEY_EFFECTIVE_DATES',
  SET_SURVEY_JOB_FAMILIES: 'MARKET_PRICE/SET_SURVEY_JOB_FAMILIES',
  SET_SURVEYS: 'MARKET_PRICE/SET_SURVEYS',
  SET_VENDORS: 'MARKET_PRICE/SET_VENDORS',
  SET_INDUSTRY: 'MARKET_PRICE/SET_INDUSTRY',
  SET_ADD_SURVEY_LOADING: 'MARKET_PRICE/SET_ADD_SURVEY_LOADING',
  SET_SURVEY_LEVELS_MAPPED: 'MARKET_PRICE/SET_SURVEY_LEVELS_MAPPED',
  MERGE_SURVEY_SEARCH_SCORES: 'MARKET_PRICE/MERGE_SURVEY_SEARCH_SCORES',
};

const setLoading = (loading) => ({
  type: ActionTypes.SET_ADD_SURVEY_LOADING,
  payload: {
    loading,
  },
});

export const setAddSurveyData = (data) => ({
  type: ActionTypes.SET_SURVEY_DATA,
  payload: data,
});

export const resetAddSurveyData = () => ({
  type: ActionTypes.RESET_SURVEY_DATA,
});

export const mergeAddSurveySearchScores = (scoredRows) => ({
  type: ActionTypes.MERGE_SURVEY_SEARCH_SCORES,
  payload: scoredRows,
});

export const setSurveyFilterData = (data) => ({
  type: ActionTypes.SET_SURVEY_FILTER_DATA,
  payload: data,
});

export const fetchAddSurveyData = (compositeId, filter, pagination, sorting) => async (dispatch) => {
  dispatch(setLoading(true));

  try {
    const { globalLevel, ...restFilter } = filter;

    const response = await runCancelableQuery('searchSurveyJobData', () => SurveyJobService.listJobsData({
      requestBody: {
        ...restFilter,
        ...pagination,
        globalLevel: globalLevel.length && globalLevel[0] === GLOBAL_LEVEL_NOT_APPLICABLE ? null : globalLevel,
        compositeId,
        sort: sorting,
      },
    }));

    if (response) {
      const { rows, count } = response;

      dispatch(setAddSurveyData({ data: rows, count }));
      dispatch(setLoading(false));

      return rows;
    }
  } catch (err) {
    reportError(err);
    showNotification('Failed to load Add Survey Data', 'error');
  }

  dispatch(setLoading(false));

  return [];
};

export const loadAddSurveyFilters = (searchParams) => async (dispatch) => {
  try {
    const { effectiveDates, jobFamilies, globalLevels, careerLevels, mappedJobLevels, mappedJobGlobalLevels, surveys, vendors } = await SurveyService.filters(searchParams);
    dispatch(setSurveyFilterData({
      effectiveDates,
      jobFamilies,
      globalLevels,
      careerLevels,
      mappedJobLevels,
      mappedJobGlobalLevels,
      surveys,
      vendors,
    }));
  } catch (err) {
    reportError(err);
  }
};

const initialState = {
  data: [],
  count: 0,
  effectiveDates: [],
  jobFamilies: [],
  careerLevels: [],
  globalLevels: [],
  mappedJobLevels: [],
  mappedJobGlobalLevels: [],
  surveys: [],
  vendors: [],
  loading: false,
  mappedSurveyLevels: [],
};

export const addSurveyReducer = createReducer(
  initialState,
  {
    [ActionTypes.SET_SURVEY_DATA]: (state, { payload: { data, count } }) => {
      return { ...state, data: data, count };
    },
    [ActionTypes.RESET_SURVEY_DATA]: () => {
      return initialState;
    },
    [ActionTypes.MERGE_SURVEY_SEARCH_SCORES]: (state, { payload }) => {
      const scoreById = new Map(payload.map(({ id, searchScore }) => [id, searchScore]));

      return {
        ...state,
        data: state.data.map((row) => (
          scoreById.has(row.id) ? { ...row, searchScore: scoreById.get(row.id) } : row
        )),
      };
    },
    [ActionTypes.SET_ADD_SURVEY_LOADING]: (state, { payload }) => {
      return { ...state, ...payload };
    },
    [ActionTypes.SET_SURVEY_FILTER_DATA]: (state, { payload }) => {
      return { ...state, ...payload };
    },
    [ActionTypes.SET_SURVEY_LEVELS_MAPPED]: (state, { payload }) => {
      return { ...state, mappedSurveyLevels: payload };
    },
  },
);
